import { AvailableTemplate, DataCell, ExcelExportDto, TableColumn } from '@ecosystem/export-api';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import { ExcelRowArgs, ExportChartArgs, ExportTableParams } from '../Model';
import { saveFile } from '../SaveFile';
import { ExportContext, Filters, PageName } from '@utils/pageConfig/Types';
import { getContext, getFilename } from '../ExportUtils';
import { PerformanceAnalysisView } from '@components/performance-analysis-card/Types';
import { ExportTrackingPageView } from '../export-tracking/ExportTracking';
import {
  getORTableExportColumn,
  getORTableExportRow,
  getProductsCompareExportColumn,
  getProductsCompareExportRow,
  getTableExportColumn,
  getTableExportRow,
  getTopSellingExportColumn,
  getTrendingAssistanceNonDeepDiveRows,
  getTopSellingExportRow,
  getTradingAssistantExportColumn,
  getTrendingAssistanceDeepDiveRows,
} from './ExportTableExcel';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';

const createExcel = async (
  {
    filename,
    exportFn,
    appliedCountry,
    appliedPg,
    data,
    summaryData,
    factRevenue,
    excelRowArgs,
    pageName,
    worksheetName,
    pageView,
    pagesConfig,
    volume = DEFAULT_VOLUME,
    loadedProductGroups,
    loadedCountries,
    retailClientName,
    isDeepDiveView,
    segmentBy,
    sortedDeepDiveBrands,
  }: ExportTableParams,
  prodLeaderboardFeatureProductFilters: Filters[],
) => {
    const hasGroups = !!(summaryData && summaryData.length > 1);
    const getDataColumns = (exportPageName: PageName): TableColumn[] => {
    if (exportPageName === PageName.OPPORTUNITY_REVIEW) {
      return getORTableExportColumn(factRevenue, volume, hasGroups);
    }
    if (exportPageName === PageName.RI_PRODUCT) {
      return getTopSellingExportColumn(factRevenue.currencySymbol, data, prodLeaderboardFeatureProductFilters, hasGroups);
    }
    if (exportPageName === PageName.RI_PRODUCT_COMPARE) {
      return getProductsCompareExportColumn(data);
    }
    if (exportPageName === PageName.TRADING_ASSISTANT) {
      return getTradingAssistantExportColumn(factRevenue.currencySymbol, retailClientName, isDeepDiveView, segmentBy);
    }
    return getTableExportColumn(factRevenue, volume, hasGroups);
  };

  const dataColumns: TableColumn[] = getDataColumns(pageName);
  const getDataRows = (
    exportPageName: PageName,
    exportPageView: ExportTrackingPageView | undefined,
    responseData: any,
    deepDiveBrands: string[] = [],
  ): DataCell[][] => {
    if (exportPageName === PageName.OPPORTUNITY_REVIEW) {
      return getORTableExportRow(responseData, hasGroups);
    }
    if (exportPageName === PageName.RI_PRODUCT) {
      return getTopSellingExportRow(responseData, prodLeaderboardFeatureProductFilters, hasGroups);
    }
    if (exportPageName === PageName.RI_PRODUCT_COMPARE) {
      return getProductsCompareExportRow(responseData, factRevenue.currencySymbol);
    }
    if (exportPageName === PageName.TRADING_ASSISTANT) {
      return !isDeepDiveView
        ? getTrendingAssistanceNonDeepDiveRows(responseData)
        : getTrendingAssistanceDeepDiveRows(responseData, deepDiveBrands);
    }
    //Other than Opportunity Review and Product page will follow default
    return getTableExportRow(responseData, hasGroups);
  };
  const dataRows: DataCell[][] = getDataRows(pageName, pageView, data, sortedDeepDiveBrands);

  //Get summary data rows to become sticky on top
  const summaryRows: DataCell[][] = getDataRows(pageName, pageView, summaryData ?? [], sortedDeepDiveBrands);

  const getColumnWidths = (): { columnNumber: number; width: number }[] => {
    const REST_COLUMN_WIDTH = pageName === PageName.OPPORTUNITY_REVIEW ? 15 : 20;
    if (pageName === PageName.OPPORTUNITY_REVIEW && pageView === ExportTrackingPageView.BubbleChart) {
      return [
        { columnNumber: 1, width: 25 },
        { columnNumber: 2, width: 35 },
        ...Array(dataColumns.length)
          .fill(3)
          .map((_, i) => ({ columnNumber: i + 3, width: 25 })),
      ];
    }
    return [
      //set only first column width to 25, rest all to 15/20
      { columnNumber: 1, width: 25 },
      ...Array(dataColumns.length)
        .fill(2)
        .map((_, i) => ({ columnNumber: i + 2, width: REST_COLUMN_WIDTH })),
    ];
  };

  const exportContext: ExportContext = {
    appliedCountry: appliedCountry,
    appliedPg: appliedPg,
    timePeriod: excelRowArgs.timePeriod,
    comparisonTimePeriod: excelRowArgs.comparisonTimePeriod,
    periodType: excelRowArgs.periodType,
    showPerformanceWithin: excelRowArgs.showPerformanceWithin,
    appliedFiltersText: excelRowArgs.appliedFiltersText,
    pageName: pageName,
    pagesConfig: pagesConfig,
    loadedProductGroups,
    loadedCountries,
  };

  const exportData: ExcelExportDto = {
    template: AvailableTemplate.Excel,
    name: filename,
    worksheets: [
      {
        worksheetName: worksheetName,
        context: getContext(exportContext),
        data: {
          dataRows,
          dataColumns,
          summaryRows,
          columnWidths: getColumnWidths(),
          currencySymbol: factRevenue.currencySymbol,
        },
      },
    ],
  };

  const exportFile = await exportFn(exportData);
  saveFile(exportFile);
};

export async function exportTable(
  args: ExportChartArgs,
  prodLeaderboardFeatureProductFilters: Filters[],
): Promise<void> {
  const {
    exportFn,
    exportFormats,
    title,
    appliedCountry,
    appliedPg,
    data,
    summaryData,
    timePeriod,
    comparisonTimePeriod,
    factRevenue,
    periodType,
    showPerformanceWithin,
    appliedFiltersText,
    selectedKpi,
    pageName,
    pageView,
    pagesConfig,
    volume = DEFAULT_VOLUME,
    loadedProductGroups,
    loadedCountries,
    retailClientName,
    isDeepDiveView,
    segmentBy,
    sortedDeepDiveBrands,
  } = args;
  const rowArgs: ExcelRowArgs = {
    timePeriod,
    comparisonTimePeriod,
    periodType,
    showPerformanceWithin,
    appliedFiltersText,
    selectedKpi,
  };

  if (exportFormats.includes(ExportFormat.Xlsx)) {
    const filename = getFilename({
      title,
      appliedCountry,
      appliedPg,
      timeframe: timePeriod,
      comparisonTimePeriod,
      exportFormat: ExportFormat.Xlsx,
      selectedKpi,
      viewType: PerformanceAnalysisView.Table,
      pageName: pageName,
    });

    await createExcel(
      {
        filename,
        exportFn,
        appliedCountry,
        appliedPg,
        data,
        summaryData,
        factRevenue,
        excelRowArgs: rowArgs,
        pageName,
        worksheetName:
          pageName === PageName.OPPORTUNITY_REVIEW || pageName === PageName.PERFORMANCE_ANALYSIS ? title : pageName,
        pageView,
        pagesConfig,
        volume,
        loadedProductGroups,
        loadedCountries,
        retailClientName,
        isDeepDiveView,
        segmentBy,
        sortedDeepDiveBrands,
      },
      prodLeaderboardFeatureProductFilters,
    );
  }
}
